import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthData } from 'app/shared/models/class/auth/auth-data';
import { AuthResponse } from 'app/shared/models/class/auth/auth-response';
import { AuthUser } from 'app/shared/models/class/auth/auth-user';
import { InfosUser } from 'app/shared/models/class/auth/infosUser';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { DataService } from '../data/data.service';
import { IsAdminService } from '../isAdmin/is-admin.service';
import { UiService } from '../ui/ui.service';
import * as CryptoJS from 'crypto-js';
import { RoleType } from 'app/shared/models/class/auth/role-type.enum';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  authChanged = new Subject<boolean>();

  private identityApiUrl: string = environment.identityApiUrl;
  private apiUrl: string = environment.apiUrl;

  private tokenKey: string = environment.tokenKey;
  private rfTokenKey: string = environment.rfTokenKey;
  private tokenExpKey: string = environment.tokenExpKey;
  private afterLoginUrl = '/home';

  isLog$ = new BehaviorSubject<boolean>(false);
  logInProgress$ = new BehaviorSubject<boolean>(false);
  currentUser = new BehaviorSubject<InfosUser>(null as any);

  public get currentUserValue(): InfosUser {
    return this.currentUser.value;
  }
  acces_token!: string;

  constructor(
    private router: Router,
    private http: HttpClient,
    private jwtHelperSrv: JwtHelperService,
    private isAdminService: IsAdminService,
    private dataService: DataService,
    private uiService: UiService
  ) {}

  login(authData: AuthData): void {
    const hashedPassword = this.cryptString(authData.password);
    const httpParams = new HttpParams()
      .append('username', authData.username)
      .append('password', hashedPassword)
      .append('grant_type', 'admin')
      .append('client_id', 'web');

    this.logInProgress$.next(true);

    this.http
      .post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
      .subscribe(
        (result) => {
          this.saveToken(
            result.access_token,
            result.refresh_token,
            result.expires_in
          );
          this.authChanged.next(true);
          this.isAdminService.ifIsAdmin(this.getDecodedToken().role);
          this.currentUser.next(this.getDecodedToken());

          this.router.navigate(['/admin']);
          this.isLog$.next(true);
          this.logInProgress$.next(false);
        },
        (error) => {
          this.uiService.showSnackbar('Login ou mot de passe incorrect');
          this.logInProgress$.next(false);
        }
      );
  }

  loginSocial(
    authTokenSocial: string,
    socialId: string,
    rs: string,
    email: string
  ) {
    const httpParams = new HttpParams()
      .append('grant_type', 'social')
      // .append('scope', 'api offline_access')
      .append('client_id', 'web')
      .append('id', socialId)
      .append('type', rs)
      .append('token', authTokenSocial)
      .append('email', email);

    return this.http
      .post<AuthResponse>(
        `${environment.identityApiUrl}connect/token`,
        httpParams
      )
      .pipe(
        map((response) => {
          if (response === null) {
          }

          this.saveToken(
            response.access_token,
            response.refresh_token,
            response.expires_in
          );
          this.authChanged.next(true);
          this.isAdminService.ifIsAdmin(this.getDecodedToken().role);
          this.currentUser.next(this.getDecodedToken());

          return this.setResponseLogin(response);
        }),
        tap((result) => {
          this.saveToken(
            result.access_token,
            result.refresh_token,
            result.expires_in
          );
        })
      );
  }

  // signup(newUser: SocialUserSignUp): Observable<UserSignUp> {
  //   return this.http.post<UserSignUp>(`${environment.apiUrl}/users`, newUser);
  // }

  /**
   * Called if user forgot his password
   * Send email with link to reset password
   * @param username: username from request form
   */
  requestPasswordReset(username: string): Observable<void> {
    const httpParams = new HttpParams().append('username', username);

    return this.http.post<void>(
      this.apiUrl + 'users/password-reset-request',
      httpParams
    );
  }

  /**
   * Called if welcome link is expired or manually ask for new email in user management page
   * Send email with welcome link
   * @param username: username from email link or users list
   */
  requestPasswordInit(username: string): Observable<void> {
    const httpParams = new HttpParams().append('username', username);

    return this.http.post<void>(
      this.apiUrl + 'users/password-init-request',
      httpParams
    );
  }

  updatePassword(resetData: {
    currentPassword: string;
    newPassword: string;
  }): Observable<string> {
    const hashedCurrentPassword = this.cryptString(resetData.currentPassword);
    const hashedNewPassword = this.cryptString(resetData.newPassword);

    const httpParams = new HttpParams()
      .append('current_password', hashedCurrentPassword)
      .append('new_password', hashedNewPassword);

    return this.http
      .post<string>(this.apiUrl + 'users/password', httpParams)
      .pipe(
        tap(() => {
          this.afterPwdReset(resetData.newPassword);
        })
      );
  }

  resetPassword(
    userId: string,
    token: string,
    newPassword: string
  ): Observable<void> {
    const hashedNewPassword = this.cryptString(newPassword);

    // Send data in body in place of http params to avoid mess in token serialisation due to special characters
    const body = {
      token,
      new_password: hashedNewPassword,
    };

    return this.http
      .post<void>(`${this.apiUrl}users/${userId}/password-reset`, body)
      .pipe(
        tap(() => {
          this.router.navigate(['/login']);
        })
      );
  }

  refreshToken(): Observable<AuthResponse> {
    const refreshToken = localStorage.getItem(this.rfTokenKey);
    const httpParams = new HttpParams()
      .append('grant_type', 'refresh_token')
      // .append('scope', 'LVHDFApi')
      .append('client_id', 'web')
      .append('refresh_token', refreshToken as string);

    return this.http
      .post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
      .pipe(
        tap((result) => {
          this.saveToken(
            result.access_token,
            result.refresh_token,
            result.expires_in
          );
          this.authChanged.next(true);
        })
      );
  }

  logout(prevUrl?: string): void {
    if (prevUrl) {
      this.afterLoginUrl = prevUrl;
    }

    localStorage.clear();
    this.authChanged.next(false);
    this.isLog$.next(false);
    this.router.navigate(['/signin']);
  }

  isAuth(): boolean {
    const decodedToken = this.getDecodedToken();

    if (!decodedToken || decodedToken.upd_pwd_req === true) {
      this.currentUser.next(null as any);
      this.refreshToken();
      return false;
    }

    this.currentUser.next(decodedToken);

    return !this.isTokenExpired();
  }

  getDecodedToken(): any {
    const token = localStorage.getItem(environment.tokenKey);
    if (token) {
      const userInfos: InfosUser | null = this.jwtHelperSrv.decodeToken(token);
      return userInfos;
    }

    return null;
  }

  getUser(): AuthUser {
    const decodedToken = this.getDecodedToken();
    if (!decodedToken) {
      return null as any;
    }

    return new AuthUser(decodedToken.name);
  }

  // isAuthorized(): boolean {
  //   const token = this.getDecodedToken();

  //   if (!token) {
  //     return false;
  //   }

  //   // authorised
  //   return true;
  // }
  isAuthorized(roles: RoleType[], clients?: string[]): boolean {
    const token = this.getDecodedToken();

    if (!token.role) {
      return false;
    }
    const found = roles.some((r) => token.role.includes(r));

    if (found) {
      return true;
    }

    // role not authorised
    return false;
  }

  private saveToken(
    accessToken: string,
    refreshToken: string,
    deadline: number
  ): void {
    const tokenExpDate = new Date();
    tokenExpDate.setSeconds(tokenExpDate.getSeconds() + deadline);

    localStorage.setItem(this.tokenExpKey, tokenExpDate.toString());
    localStorage.setItem(this.tokenKey, accessToken);
    localStorage.setItem(this.rfTokenKey, refreshToken);
  }

  private afterPwdReset(newPassword: string): void {
    const decodedToken = this.getDecodedToken();

    if (decodedToken) {
      this.login({ username: decodedToken.name, password: newPassword });
    } else {
      this.logout();
    }
  }

  private isTokenExpired(): boolean {
    const tokenExpDate = new Date(
      localStorage.getItem(this.tokenExpKey) as string
    );
    const now = new Date();
    return tokenExpDate < now;
  }

  public cryptString(stringTocrypt: string): string {
    return CryptoJS.SHA256(stringTocrypt).toString();
  }

  private setResponseLogin(response: AuthResponse): AuthResponse {
    localStorage.setItem(environment.tokenKey, response.access_token);
    localStorage.setItem(environment.rfTokenKey, response.refresh_token);
    // const user: InfosUser = this.jwtHelperSrv.decodeToken(response.access_token);
    // this.acces_token = response.access_token;
    // this.currentUser.next(user);
    return response;
  }
}
